import { defineComponent as _defineComponent } from 'vue'
import { unref as _unref, toDisplayString as _toDisplayString, createElementVNode as _createElementVNode, resolveComponent as _resolveComponent, createVNode as _createVNode, renderList as _renderList, Fragment as _Fragment, openBlock as _openBlock, createElementBlock as _createElementBlock, withModifiers as _withModifiers, createBlock as _createBlock, createCommentVNode as _createCommentVNode, withCtx as _withCtx } from "vue"

const _hoisted_1 = { class: "LandingListTableLayoutMenu__wrapper column no-wrap bg-white shadow-2 rounded-16 dynamic-zoom" }
const _hoisted_2 = { class: "fs-13px fw-500 text-cool-gray-80" }
const _hoisted_3 = ["onDragstart", "onDragover"]
const _hoisted_4 = ["onClick"]
const _hoisted_5 = ["onClick"]
const _hoisted_6 = ["onClick"]

import useDrag from 'src/hook/useDrag'
import { reactive, watch } from 'vue'
import _ from 'lodash'
import translate from 'src/common/helpers/language/translate'
import globalTranslateObject from 'src/translate/global.translate'
import { ITableColumn } from 'src/common/interfaces'

interface IProps {
  columns?: ITableColumn[]
  order?: string[]
  toggleHistory?: string[]
  dynamicColumns?: IDynamicTableColumn[]
  defaultColumns?: ITableColumn[]
  persistentColumns?: string[]
  maxCol?: number
}

type Emits = { reorder: [layoutData: IColumnLayoutData] }

interface IState {
  columns: ITableColumn[]
  dynamicColumns: IDynamicTableColumn[]
  order: string[]
  toggleHistory: string[]
}

export interface IColumnLayoutData {
  columns: ITableColumn[]
  order: string[]
  toggleHistory: string[]
  dynamicColumns: IDynamicTableColumn[]
}

export interface IDynamicTableColumn extends ITableColumn {
  id: number
  isActive: boolean
}


export default /*@__PURE__*/_defineComponent({
  ...{ name: 'LandingListTableColumnLayout' },
  __name: 'LandingListTableColumnLayout',
  props: {
    columns: { default: () => [] },
    order: { default: () => [] },
    toggleHistory: { default: () => [] },
    dynamicColumns: { default: () => [] },
    defaultColumns: { default: () => [] },
    persistentColumns: { default: () => [] },
    maxCol: { default: 10 }
  },
  emits: ["reorder"],
  setup(__props: any, { emit: __emit }) {


const props = __props
const emit = __emit

const { t: globalT } = translate(globalTranslateObject)

const dragHook = useDrag()
const fixedColumns = new Set(['actions', 'layout', 'id', 'selection'])

const state = reactive<IState>({
  columns: [],
  dynamicColumns: [],
  order: [],
  toggleHistory: [],
})

function handleDrag({ newItems }: { newItems: IDynamicTableColumn[] }) {
  state.dynamicColumns = newItems
  updateOrder()
  updateColumns()
}

function handleColumnToggle(column: IDynamicTableColumn) {
  if (!props.persistentColumns.includes(column.name)) {
    column.isActive = !column.isActive
    toggleColumn(column)
  }
}

function toggleColumn(column: IDynamicTableColumn) {
  const activeCount = state.dynamicColumns.reduce((count, col) => count + (col.isActive ? 1 : 0), 0)
  if (activeCount > 0) {
    if (column.isActive && activeCount > props.maxCol) {
      const columnName = state.toggleHistory.shift()
      const targetColumnToDeactivate = state.dynamicColumns.find((col) => col.name === columnName)
      if (targetColumnToDeactivate) targetColumnToDeactivate.isActive = false
    }

    if (column.isActive) state.toggleHistory.push(column.name)
    else {
      const index = state.toggleHistory.indexOf(column.name)
      if (index !== -1) state.toggleHistory.splice(index, 1)
    }
  } else {
    column.isActive = true
    state.toggleHistory = [column.name]
  }

  updateOrder()
  updateColumns()
}

function updateColumns() {
  const model = []
  let reorderIndex = 0
  for (const column of state.columns) {
    if (fixedColumns.has(column.name)) model.push(column)
    else {
      const orderedColumn = state.dynamicColumns[reorderIndex++]
      const index = state.order.indexOf(orderedColumn.name)
      if (index !== -1) model.push(orderedColumn)
    }
  }

  emit('reorder', {
    columns: model,
    order: state.order,
    toggleHistory: state.toggleHistory,
    dynamicColumns: state.dynamicColumns,
  })
}

function updateOrder() {
  state.order = []
  for (const col of state.dynamicColumns) if (col.isActive) state.order.push(col.name)
}

function initOrder() {
  state.order = []
  for (const col of props.defaultColumns)
    if (
      (!fixedColumns.has(col.name) && state.order.length < props.maxCol) ||
      props.persistentColumns.includes(col.name)
    )
      state.order.push(col.name)
}

function reorderColumns(modifiedColumns: IDynamicTableColumn[]) {
  const allColumns = new Array(state.columns.length)
  let fixedColumnsCounter = 0
  const activeColumns = new Array(state.order.length)
  const inactiveColumns: IDynamicTableColumn[] = []

  const isToggleHistoryInit = !props.toggleHistory.length
  const isDynamicColumnsInit = !props.dynamicColumns.length
  if (!isToggleHistoryInit) state.toggleHistory = props.toggleHistory
  if (!isDynamicColumnsInit) state.dynamicColumns = props.dynamicColumns

  state.columns.forEach((column, counter) => {
    if (fixedColumns.has(column.name)) {
      allColumns[counter] = _.omit(column, ['id', 'isActive'])
      fixedColumnsCounter++
    } else {
      const index = state.order.indexOf(column.name)
      if (index !== -1) {
        const existingColumn = modifiedColumns.find((col) => col.name === state.order[index])
        const activeCount = state.dynamicColumns.filter((col) => col.isActive).length
        if (activeCount < props.maxCol && existingColumn) {
          existingColumn.isActive = true
          if (isToggleHistoryInit && !props.persistentColumns.includes(column.name))
            state.toggleHistory.push(existingColumn.name)
        }

        if (isDynamicColumnsInit) activeColumns[index] = existingColumn

        allColumns[index + fixedColumnsCounter] = _.omit(existingColumn, ['id', 'isActive'])
      } else {
        const existingColumn = modifiedColumns.find((col) => col.name === column.name)
        if (existingColumn) {
          existingColumn.isActive = false
          if (isDynamicColumnsInit) inactiveColumns.push(existingColumn)
        }
      }
    }
  })

  const model = allColumns.filter(Boolean)
  if (isDynamicColumnsInit) state.dynamicColumns = activeColumns.filter(Boolean).concat(inactiveColumns)

  emit('reorder', {
    columns: model,
    order: state.order,
    toggleHistory: state.toggleHistory,
    dynamicColumns: state.dynamicColumns,
  })
}

function initColumns() {
  const modifiedColumns: IDynamicTableColumn[] = []
  if (!props.order.length) initOrder()
  else state.order = props.order

  state.columns.forEach((col, index) => {
    if (!fixedColumns.has(col.name)) {
      const column = {
        ...col,
        id: index + 1,
        isActive:
          modifiedColumns.length < props.maxCol - props.persistentColumns.length ||
          props.persistentColumns.includes(col.name),
      }
      modifiedColumns.push(column)
    }
  })
  reorderColumns(modifiedColumns)
}

function reset() {
  emit('reorder', {
    columns: props.defaultColumns,
    order: [],
    toggleHistory: [],
    dynamicColumns: [],
  })
}

watch(
  () => props.columns,
  (newColumns) => {
    state.columns = newColumns
    initColumns()
  },
  { immediate: true, deep: true },
)

return (_ctx: any,_cache: any) => {
  const _component_k_btn = _resolveComponent("k-btn")!
  const _component_k_divider = _resolveComponent("k-divider")!
  const _component_k_icon = _resolveComponent("k-icon")!
  const _component_k_checkbox = _resolveComponent("k-checkbox")!
  const _component_q_popup_proxy = _resolveComponent("q-popup-proxy")!

  return (_openBlock(), _createBlock(_component_q_popup_proxy, {
    class: "LandingListTableLayoutMenu",
    offset: [16, 8],
    "transition-show": "jump-up",
    "transition-hide": "jump-down"
  }, {
    default: _withCtx(() => [
      _createElementVNode("div", _hoisted_1, [
        _createElementVNode("div", {
          class: "LandingListTableLayoutMenu__reset row items-center justify-between q-px-md q-py-12 cursor-pointer transition",
          onClick: reset
        }, [
          _createElementVNode("span", _hoisted_2, _toDisplayString(_unref(globalT)('back_to_default')), 1),
          _createVNode(_component_k_btn, {
            model: "link",
            icon: "refresh",
            "icon-size": "20px",
            "custom-color": "cool-gray-70",
            class: "LandingListTableLayoutMenu__reset-btn"
          })
        ]),
        _createVNode(_component_k_divider, {
          size: "lg",
          color: "off-white-20"
        }),
        (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(state.dynamicColumns, (col, index) => {
          return (_openBlock(), _createElementBlock("div", {
            key: index,
            class: "q-pa-xs",
            draggable: true,
            onDragstart: ($event: any) => (_unref(dragHook).handleDragStart(col)),
            onDragend: _cache[0] || (_cache[0] = ($event: any) => (
          _unref(dragHook).handleDragEnd({
            items: state.dynamicColumns,
            callback: handleDrag,
          })
        )),
            onDragover: ($event: any) => (_unref(dragHook).handleDragOver(col))
          }, [
            _createElementVNode("div", {
              class: "LandingListTableLayoutMenu__column rounded-12 row items-center justify-between q-px-12 q-py-sm transition cursor-pointer",
              onClick: _withModifiers(($event: any) => (handleColumnToggle(col)), ["self"])
            }, [
              _createElementVNode("div", {
                class: "row items-center gap-sm flex-1",
                onClick: _withModifiers(($event: any) => (handleColumnToggle(col)), ["self"])
              }, [
                _createVNode(_component_k_icon, {
                  name: "arrangement",
                  color: "cool-gray-60",
                  size: "24px",
                  class: "LandingListTableLayoutMenu__column-handle cursor-grab transition"
                }),
                _createElementVNode("span", {
                  class: "LandingListTableLayoutMenu__column-label fs-13px fw-500 text-cool-gray-80 flex-1 transition",
                  onClick: ($event: any) => (handleColumnToggle(col))
                }, _toDisplayString(col.label), 9, _hoisted_6)
              ], 8, _hoisted_5),
              (!__props.persistentColumns.includes(col.name))
                ? (_openBlock(), _createBlock(_component_k_checkbox, {
                    key: 0,
                    modelValue: col.isActive,
                    "onUpdate:modelValue": [($event: any) => ((col.isActive) = $event), ($event: any) => (toggleColumn(col))]
                  }, null, 8, ["modelValue", "onUpdate:modelValue"]))
                : _createCommentVNode("", true)
            ], 8, _hoisted_4)
          ], 40, _hoisted_3))
        }), 128))
      ])
    ]),
    _: 1
  }))
}
}

})